import { get, noop } from 'lodash'
import { pluck, tap } from 'rxjs/operators'
import { faCircleNotch, faFilePdf, faPrint, faRetweet } from '@fortawesome/free-solid-svg-icons'
import { PropType } from 'vue'
import Component from 'vue-class-component'

import { SearchResult, PrintableReport, Wemi } from '@skinsoft/types'
import { itemSelectors } from '@skinsoft/selectors'
import { ItemDataStore, PrintableReportDataStore, PrintableReportFormat, exportItem } from '@skinsoft/entity'
import { ZPLGenerator } from '@skinsoft/zpl-generator'
import { ReactiveVue } from '@skinsoft/reactive-vue'
import { SelectItemMultipleForm } from '../../../../../entity/component/select-item-multiple-modal/select-item-multiple-form'
import { SELECT_ITEM_MULTIPLE_FORM_ITEM_SELECTED } from '../../../../../events'

declare const config 
const ZPL_PRINTER_CONFIG = config.zpl_printer_config 
const PAGE_SIZE = 10
const ENTITY_TYPES = {
    physicalMuseumItem: '595ca6a7857aba0001ac6442',
    physicalMuseumContainer: '595ca6a7857aba0002ac6444'
}

require('./bar-code-editor.scss')

@Component({
    components: {
        sSelectItemMultipleForm: SelectItemMultipleForm,
    },
    props: {
        types: Array as PropType<string[]>,
    },
    template: require('./bar-code-editor.html'),
})
export class BarCodeEditor extends ReactiveVue {
    public items: Array<Wemi | SearchResult> = []
    public maxItemNumber = 0
    public printableReports: PrintableReport[] = []
    public filteredPrintableReports: PrintableReport[] = []
    public selectedReportId: string | null = null

    private types: Array<string>
    private shouldBeRotated: boolean = false
    private scale: number = 1.0
    private fileName: string = ""

    private rotationIcon = faRetweet
    private loadingIcon = faCircleNotch
    private pdfIcon = faFilePdf
    private printerIcon = faPrint

    private $itemDataStore: ItemDataStore
    private $printableReportDataStore: PrintableReportDataStore

    private SELECT_ITEM_MULTIPLE_FORM_ITEM_SELECTED = SELECT_ITEM_MULTIPLE_FORM_ITEM_SELECTED

    private mounted() {
        this.performSearch()
        this.$printableReportDataStore.getAllReportsOfTypesAndFormats(
            [
                ENTITY_TYPES.physicalMuseumItem,
                ENTITY_TYPES.physicalMuseumContainer,
            ], 
            [PrintableReportFormat.INDIVIDUAL_LABEL]
        ).subscribe(reports => {
            this.printableReports = reports
            this.filteredPrintableReports = this.printableReports
            this.selectedReportId = this.printableReports[0]?.id
        })
    }

    private onItemSelected(item: any) {
        let itemType = ''

        if(item.entityType) {
            itemType = item.entityType.toLowerCase()
        } else if(item.itemType) {
            itemType = item.itemType.toLowerCase()
        }

        switch(itemType) {
            case 'physicalmuseumitem':
                this.filteredPrintableReports = this.printableReports.filter(report => {
                    // @ts-ignore
                    if(report.toEntity) {
                        // @ts-ignore
                        return report.toEntity === ENTITY_TYPES.physicalMuseumItem
                    } else {
                        return true
                    }
                })
                break
            case 'physicalcontaineritem':
                this.filteredPrintableReports = this.printableReports.filter(report => {
                    // @ts-ignore
                    if(report.toEntity) {
                        // @ts-ignore
                        return report.toEntity === ENTITY_TYPES.physicalMuseumContainer
                    } else {
                        return true
                    }
                })
                break
        }
    }

    private async onItemSelectedZpl(item: any, actionBefore: () => void = noop) {
        if (this.selectedReportId) {
            const printableReport = this.printableReports.find(printableReport => printableReport.id === this.selectedReportId)
            actionBefore()
            
            let zpl=''
            if(printableReport) {
                const zplGenerator = new ZPLGenerator(this.getAppLocale(), ZPL_PRINTER_CONFIG.height, ZPL_PRINTER_CONFIG.width, ZPL_PRINTER_CONFIG.dpmm)
                zpl = await zplGenerator.printableReportToZPL(printableReport, item)
            }

            let fileName = this.fileName ? this.fileName : 
                itemSelectors.inventoryNumber(item) ?? 
                get(item, 'identificationItemSchema.identifiers[0].identifier.value') ?? 
                get(item, 'inventoryNumber') ??
                get(item, 'title') ??
                item.id ?? 
                item.itemId
            // if fileName returns an object, it is not null so it passes the ?? test
            // there is probably a cleaner way to resolve the problem
            if(fileName.value) {
                fileName = fileName.value
            }
            
            
            const downloadable = new File([new Blob([zpl])], fileName, { type: 'application/octet-stream' })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(downloadable)
            link.download = `${fileName}.zpl`
            link.click()

            setTimeout(() => {
                const linkOpenApp = document.createElement('a')
                linkOpenApp.href = 'intent://#Intent;package=com.zebra.printersetup;end' // zebra printer setup android intent
                linkOpenApp.click()
            }, 500)
            this.$emit('barcode-item-selected')
            this.fileName = ""
        }
    }

    private onItemSelectedPdf(item: any, actionBefore: () => void = noop) {
        if (this.selectedReportId) {
            actionBefore()
            exportItem(item.id, this.getAppLocale(), this.selectedReportId, this.shouldBeRotated).subscribe(pdf => {
                const pdfUrl = URL.createObjectURL(pdf)
                window.open(pdfUrl)
                this.$emit('barcode-item-selected')
            })
        }
    }

    private performSearch(searchText = '', pageNumber = 0) {
        this.$itemDataStore
            .searchAll(
                {
                    queryText: searchText,
                    pageSize: PAGE_SIZE,
                    types: this.types,
                    page: pageNumber
                }
            )
            .pipe(
                tap(searchResult => (this.maxItemNumber = searchResult.totalSize)),
                pluck('allData'),
            )
            .subscribe(items => {
                this.items = items
                this.filteredPrintableReports = this.printableReports
            })
    }
}
